<template>
  <div class="production production-list-daily">
    <div class="table-container">
      <div class="daily-export-mobile-actions">
        <button
          @click="exportToCSV"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📄 CSV
        </button>
        <button
          @click="exportToExcel"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📊 Excel
        </button>
      </div>
      <div class="daily-export-desktop-actions">
        <button @click="exportToCSV" class="desktop-export-btn">📄 CSV</button>
        <button @click="exportToExcel" class="desktop-export-btn">
          📊 Excel
        </button>
      </div>
      <div class="vuetable-body-wrapper">
        <vuetable
          ref="vuetable"
          :api-mode="false"
          :fields="fields"
          :per-page="perPage"
          :data-manager="dataManager"
          pagination-path="pagination"
          track-by="id"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:row-clicked="onRowClick"
        />
      </div>
    </div>
    <div
      v-if="tablePagination && tablePagination.last_page > 1"
      class="control-pagination"
    >
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(1)"
      >
        &lt;&lt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(tablePagination.current_page - 1)"
      >
        &lt;
      </button>
      <span class="pagination-status">
        Pagina {{ tablePagination.current_page }} de
        {{ tablePagination.last_page }}
      </span>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.current_page + 1)"
      >
        &gt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.last_page)"
      >
        &gt;&gt;
      </button>
    </div>

    <!-- Diálogo de opciones -->
    <dialog ref="optionsDialog">
      <h2 class="dialog-title">¿Qué deseas hacer?</h2>
      <div class="icon-options">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click="closeDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="editIcon"
            alt="Editar"
            class="icon edit"
            @click="openEditDialog"
          />
          <p class="icon-label">Editar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="deleteIcon"
            alt="Eliminar"
            class="icon eliminar"
            @click="confirmDelete"
          />
          <p class="icon-label">Eliminar</p>
        </div>
      </div>
    </dialog>

    <!-- Diálogo de edición -->
    <dialog ref="editDialog">
      <h2 class="dialog-title">Editar campos</h2>

      <!-- Selector de finca: staff y owners con más de una finca -->
      <div class="edit-section" v-if="canChangeFarmland">
        <label class="edit-label">Finca:</label>
        <select v-model="editFarmlandId" class="edit-input">
          <option v-for="fl in availableFarmlands" :key="fl.id" :value="fl.id">
            {{ fl.name }}
          </option>
        </select>
      </div>

      <!-- Selector de zona: todos los usuarios -->
      <div class="edit-section" v-if="availableZones.length">
        <label class="edit-label">Zona:</label>
        <select v-model="editZoneId" class="edit-input">
          <option :value="null">— Sin zona —</option>
          <option
            v-for="zone in availableZones"
            :key="zone.id"
            :value="zone.id"
          >
            {{ zone.name }} ({{ zone.code }})
          </option>
        </select>
      </div>

      <!-- Solo para fincas cooperativas -->
      <div class="edit-section" v-if="editFarmType === 'COOPERATIVE'">
        <label class="edit-label">Modo de Producción:</label>
        <select v-model="editProductionMode" class="edit-input">
          <option
            v-for="mode in availableModes"
            :key="mode.code"
            :value="mode.code"
          >
            {{ mode.name }}
          </option>
        </select>
      </div>

      <div
        class="edit-section"
        v-if="
          editFarmType === 'COOPERATIVE' &&
          (editProductionMode === 'WEIGHT' ||
            editProductionMode === 'COUNT' ||
            editProductionMode === 'LOTE')
        "
      >
        <label class="edit-label">Categoría:</label>
        <select v-model="editCategory" class="edit-input">
          <option
            v-for="category in availableCategories"
            :key="category.code"
            :value="category.code"
          >
            {{ category.name }}
          </option>
        </select>
      </div>

      <div class="edit-section" v-if="isEditDefectCategory">
        <label class="edit-label">Subcategorías:</label>
        <div class="subcategory-options">
          <label
            v-for="subcategory in editSubcategoryOptions"
            :key="subcategory"
            class="subcategory-option"
          >
            <input
              type="checkbox"
              :value="subcategory"
              v-model="editSubcategories"
            />
            <span>{{ subcategory }}</span>
          </label>
        </div>
      </div>

      <!-- Formato: mostrar para comerciales siempre, para cooperativas solo en modo WEIGHT -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COMMERCIAL' ||
          (editFarmType === 'COOPERATIVE' &&
            editProductionMode === 'WEIGHT' &&
            !isEditDefectCategory)
        "
      >
        <label class="edit-label">Introducir Formato:</label>
        <select v-model="editFormatId" class="edit-input">
          <option v-for="format in formats" :key="format.id" :value="format.id">
            {{ format.name }}
          </option>
        </select>
      </div>

      <!-- Unidades: mostrar para comerciales siempre, para cooperativas solo en modo WEIGHT -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COMMERCIAL' ||
          (editFarmType === 'COOPERATIVE' &&
            editProductionMode === 'WEIGHT' &&
            !isEditDefectCategory)
        "
      >
        <label class="edit-label">Número de cajas:</label>
        <input type="number" v-model="editUnits" min="1" class="edit-input" />
      </div>

      <!-- Conteo de frutas: para cooperativas en modo COUNT o LOTE -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COOPERATIVE' &&
          (editProductionMode === 'COUNT' || editProductionMode === 'LOTE') &&
          !isEditDefectCategory
        "
      >
        <label class="edit-label">Número de frutas:</label>
        <input
          type="number"
          v-model="editFruitCount"
          min="1"
          class="edit-input"
        />
      </div>

      <!-- KG Totales: para cooperativas en modo COUNT o LOTE -->
      <div
        class="edit-section"
        v-if="
          editFarmType === 'COOPERATIVE' &&
          (editProductionMode === 'COUNT' || editProductionMode === 'LOTE') &&
          !isEditDefectCategory
        "
      >
        <label class="edit-label">KG Totales:</label>
        <input
          type="number"
          v-model="editUnitsWeightKg"
          min="0"
          step="0.001"
          class="edit-input"
        />
      </div>

      <!-- Fecha de creación -->
      <div class="edit-section">
        <label class="edit-label">Fecha de creación:</label>
        <input
          type="datetime-local"
          v-model="editCreatedOn"
          class="edit-input"
        />
      </div>

      <div class="icon-editar">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click="closeEditDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="okeyIcon"
            alt="Confirmar"
            class="icon okey"
            @click="confirmEdit"
          />
          <p class="icon-label">Confirmar</p>
        </div>
      </div>
    </dialog>

    <!-- Confirmación edición -->
    <dialog
      ref="editConfirmDialog"
      class="confirm-dialog"
      :class="{ error: editIsError }"
    >
      <img
        :src="editIsError ? errorIcon : exitoIcon"
        class="confirm-icon"
        :alt="editIsError ? 'Error' : 'Éxito'"
      />
      <p class="dialog-message">{{ editConfirmMessage }}</p>
      <div class="confirm-buttons">
        <button @click="closeEditConfirmDialog">Aceptar</button>
      </div>
    </dialog>

    <!-- Confirmación eliminación -->
    <dialog
      ref="deleteConfirmDialog"
      class="confirm-dialog"
      :class="{ error: deleteIsError }"
    >
      <img
        :src="deleteIsError ? errorIcon : exitoIcon"
        class="confirm-icon"
        :alt="deleteIsError ? 'Error' : 'Éxito'"
      />
      <p class="dialog-message">{{ deleteConfirmMessage }}</p>
      <div class="confirm-buttons">
        <button @click="closeDeleteConfirmDialog">Aceptar</button>
      </div>
    </dialog>
  </div>
</template>

<script>
import Vuetable from "vuetable-2/src/components/Vuetable";
import * as XLSX from "xlsx";

// Importar iconos
import cancelIcon from "@/assets/icon/cancel.png";
import editIcon from "@/assets/icon/edit.png";
import deleteIcon from "@/assets/icon/eliminate.png";
import errorIcon from "@/assets/icon/error.png";
import exitoIcon from "@/assets/icon/exito.png";
import okeyIcon from "@/assets/icon/okey.png";

const DEFAULT_OTROS_DEFECTOS_SUBCATEGORIES = [
  "Blanda",
  "Sangrante",
  "Rota",
  "Deforme",
  "Pétalo pegado",
  "Cicatriz",
];

export default {
  name: "ListProductions",
  components: {
    Vuetable,
  },
  data() {
    return {
      cancelIcon,
      editIcon,
      deleteIcon,
      okeyIcon,
      errorIcon,
      exitoIcon,
      fields: [
        { name: "farmland.name", title: "Finca" },
        { name: "zone.code", title: "Zona" },
        { name: "production_mode_display", title: "Modo" },
        { name: "category", title: "Categoría" },
        { name: "berries_defects_display", title: "Defectos" },
        { name: "format_display", title: "Formato" },
        { name: "units_display", title: "Nº Cajas" },
        { name: "fruit_count_display", title: "Nº Frutas" },
        { name: "total_display", title: "KG Totales" },
        { name: "created_on_display", title: "Hora" },
        { name: "modified_on_display", title: "Últ. edición" },
        { name: "id", title: "", dataClass: "hidden-id-field" },
      ],
      perPage: 20,
      tablePagination: null,
      exportPageSize: 200,
      // Datos
      data: [],
      selectedItem: null,
      // Edición
      editFarmType: null,
      editFormatId: null,
      editUnits: 0,
      editProductionMode: null,
      editCategory: null,
      editSubcategories: [],
      editFruitCount: 0,
      formats: [],
      availableModes: [],
      availableCategories: [],
      // Mensajes de confirmación
      editConfirmMessage: "",
      deleteConfirmMessage: "",
      // Estados de error
      editIsError: false,
      deleteIsError: false,
      editUnitsWeightKg: 0,
      isStaff: false,
      availableFarmlands: [],
      availableZones: [],
      editFarmlandId: null,
      editZoneId: null,
      editCreatedOn: "",
      isInitializingEditDialog: false,
    };
  },

  computed: {
    canChangeFarmland() {
      return this.isStaff || this.availableFarmlands.length > 1;
    },
    selectedEditCategoryObj() {
      if (!this.editCategory) return null;
      return (
        this.availableCategories.find(
          (category) => category.code === this.editCategory,
        ) || null
      );
    },
    editSubcategoryOptions() {
      const raw = this.selectedEditCategoryObj?.subcategories;
      if (Array.isArray(raw)) {
        const cleaned = raw
          .map((value) => String(value || "").trim())
          .filter((value) => value);
        if (cleaned.length > 0) return cleaned;
      }

      if (this.editCategory === "OTROS_DEFECTOS") {
        return DEFAULT_OTROS_DEFECTOS_SUBCATEGORIES;
      }

      return [];
    },
    isEditDefectCategory() {
      return (
        this.editFarmType === "COOPERATIVE" &&
        this.editSubcategoryOptions.length > 0
      );
    },
  },

  watch: {
    async editFarmlandId(newId, oldId) {
      if (!newId || newId === oldId) return;
      if (this.isInitializingEditDialog) return;

      this.editZoneId = null;
      await this.loadZonesForFarmland(newId);
      const fl = this.availableFarmlands.find(
        (f) => f.id === newId || f.id === parseInt(newId),
      );
      if (fl) this.editFarmType = fl.farm_type;
    },
    async editProductionMode(newMode, oldMode) {
      if (newMode === oldMode) return;

      // Recargar categorías para el nuevo modo
      await this.loadCategoriesForMode(newMode, this.editFarmlandId);

      // Auto-select if only one category available
      if (this.availableCategories.length === 1) {
        this.editCategory = this.availableCategories[0].code;
      } else if (this.editCategory) {
        // Si la categoría actual ya no está disponible en el nuevo modo, resetearla
        const categoryExists = this.availableCategories.some(
          (cat) => cat.code === this.editCategory,
        );
        if (!categoryExists) {
          this.editCategory = null;
        }
      }

      // Si cambia a WEIGHT y aún no cargamos formatos, cargarlos
      if (newMode === "WEIGHT") {
        if (
          this.formats.length === 0 &&
          this.selectedItem &&
          this.selectedItem.farmland_id
        ) {
          this.loadFormatsForFarmland(this.selectedItem.farmland_id);
        }
      }

      // Si cambia a COUNT o LOTE, limpiar selección de formato
      if (newMode === "COUNT" || newMode === "LOTE") {
        this.editFormatId = null;
      }

      this.syncEditSubcategoriesWithOptions();
    },
    editCategory(newCategory, oldCategory) {
      if (newCategory === oldCategory) return;
      this.syncEditSubcategoriesWithOptions();
    },
  },

  mounted() {
    // Leer is_staff del payload del token JWT (funciona con sesiones ya iniciadas)
    try {
      const token = localStorage.getItem("token");
      const payload = token ? JSON.parse(atob(token.split(".")[1])) : {};
      this.isStaff = payload.is_staff === true;
    } catch {
      this.isStaff = false;
    }
    this.$nextTick(() => {
      this.refreshProductionTable();
    });
    this.loadFarmlands();
    this.$root.$on("list", this.getProduction);
  },

  beforeDestroy() {
    this.$root.$off("list", this.getProduction);
  },

  methods: {
    // Formatea fecha y hora a un texto corto legible
    formatDateTime(value) {
      if (!value) return "";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      const pad = (num) => String(num).padStart(2, "0");
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];

      return `${pad(date.getDate())} ${
        months[date.getMonth()]
      } ${date.getFullYear()}, ${pad(date.getHours())}:${pad(
        date.getMinutes(),
      )}`;
    },

    // Paginación
    onPaginationData(paginationData) {
      this.tablePagination = paginationData || null;
    },
    onChangePage(page) {
      this.goToPage(page);
    },

    goToPage(page) {
      if (!this.$refs.vuetable) return;
      const parsedPage = Number(page);
      if (!Number.isFinite(parsedPage)) return;

      const currentPage = Number(this.tablePagination?.current_page || 1);
      const lastPage = Number(this.tablePagination?.last_page || parsedPage);
      let targetPage = Math.max(1, Math.trunc(parsedPage));

      if (Number.isFinite(lastPage) && lastPage > 0) {
        targetPage = Math.min(targetPage, lastPage);
      }

      if (targetPage === currentPage) {
        this.refreshProductionTable();
        return;
      }

      this.$refs.vuetable.changePage(targetPage);
    },

    buildProductionEndpoint(page = 1, pageSize = this.perPage) {
      return `/production/?page=${page}&page_size=${pageSize}`;
    },

    buildTablePagination(responseData, pageSize, currentPage) {
      const pagination = responseData.pagination || {};
      const total = pagination.total ?? responseData.count ?? 0;

      return {
        total,
        per_page: pagination.per_page ?? pageSize,
        current_page: pagination.current_page ?? currentPage,
        last_page:
          pagination.last_page ??
          (Math.ceil(total / (pageSize || this.perPage)) || 0),
        next_page_url: pagination.next_page_url ?? responseData.next ?? "",
        prev_page_url: pagination.prev_page_url ?? responseData.previous ?? "",
        from: pagination.from ?? 0,
        to: pagination.to ?? 0,
      };
    },

    normalizeProductionRows(items) {
      return items.map((item) => {
        const normalized = { ...item };

        normalized.created_on_display = this.formatDateTime(
          normalized.created_on,
        );
        normalized.modified_on_display = normalized.modified_on
          ? this.formatDateTime(normalized.modified_on)
          : "-";

        // Format zone: show "-" if no zone
        if (!normalized.zone) {
          normalized.zone = { name: "-", code: "-" };
        } else {
          if (!normalized.zone.name) {
            normalized.zone.name = "-";
          }
          if (!normalized.zone.code) {
            normalized.zone.code = "-";
          }
        }

        if (normalized.production_mode === "WEIGHT") {
          normalized.production_mode_display = "Peso";
        } else if (normalized.production_mode === "COUNT") {
          normalized.production_mode_display = "Conteo";
        } else if (normalized.production_mode === "LOTE") {
          normalized.production_mode_display = "Lote";
        } else {
          normalized.production_mode_display = "-";
        }

        if (!normalized.category) {
          normalized.category = "-";
        }

        normalized.berries_defects_display =
          normalized.berries_defects_display || "-";

        if (
          normalized.production_mode === "COUNT" ||
          normalized.production_mode === "LOTE"
        ) {
          normalized.format_display = "-";
          normalized.units_display = "-";
          if (normalized.units_weight) {
            normalized.total = normalized.units_weight / 1000;
            normalized.total_display =
              Math.round(normalized.total * 1000) / 1000;
          } else {
            normalized.total = 0;
            normalized.total_display = "-";
          }
          normalized.fruit_count_display = normalized.fruit_count || "-";
        } else {
          normalized.format_display = normalized.format?.name || "-";
          normalized.units_display = normalized.units || 0;
          if (normalized.format && normalized.units) {
            normalized.total =
              (normalized.units * normalized.format.weight) / 1000;
            normalized.total_display = normalized.total;
          } else {
            normalized.total = 0;
            normalized.total_display = 0;
          }
          normalized.fruit_count_display = "-";
        }

        return normalized;
      });
    },

    async dataManager(sortOrder, pagination) {
      const currentPage = pagination?.current_page || 1;
      const pageSize = pagination?.per_page || this.perPage;

      try {
        const response = await this.$http.get(
          this.$hostname + this.buildProductionEndpoint(currentPage, pageSize),
        );

        const rows = this.normalizeProductionRows(response.data.results || []);
        const tablePagination = this.buildTablePagination(
          response.data,
          pageSize,
          currentPage,
        );

        this.data = rows;
        this.tablePagination = tablePagination;

        return {
          pagination: tablePagination,
          data: rows,
        };
      } catch (error) {
        this.showEditError("Error al obtener producción diaria");
        const errorPagination = {
          total: 0,
          per_page: pageSize,
          current_page: 1,
          last_page: 0,
          next_page_url: "",
          prev_page_url: "",
          from: 0,
          to: 0,
        };
        this.data = [];
        this.tablePagination = errorPagination;

        return {
          pagination: errorPagination,
          data: [],
        };
      }
    },

    refreshProductionTable() {
      if (!this.$refs.vuetable) return;
      return this.$refs.vuetable.refresh();
    },

    async getProduction() {
      return this.refreshProductionTable();
    },

    async fetchAllProductionRows() {
      const rows = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.$http.get(
          this.$hostname +
            this.buildProductionEndpoint(currentPage, this.exportPageSize),
        );

        rows.push(...this.normalizeProductionRows(response.data.results || []));

        const pagination = this.buildTablePagination(
          response.data,
          this.exportPageSize,
          currentPage,
        );

        hasMorePages =
          pagination.last_page !== 0 && currentPage < pagination.last_page;

        if (hasMorePages) {
          currentPage += 1;
        }
      }

      return rows;
    },

    // Selección de fila
    onRowClick(data) {
      if (data.data) {
        this.selectedItem = { ...data.data };
      } else {
        this.selectedItem = { ...data };
      }

      let farmlandId = null;

      if (this.selectedItem.farmland_id) {
        farmlandId = this.selectedItem.farmland_id;
      } else if (this.selectedItem.farmland && this.selectedItem.farmland.id) {
        farmlandId = this.selectedItem.farmland.id;
      }

      if (!farmlandId) {
        this.showEditError("Error: Datos de finca no disponibles");
        return;
      }

      this.selectedItem.farmland_id = farmlandId;

      this.$refs.optionsDialog.showModal();
    },

    // Diálogos generales
    closeDialog() {
      this.selectedItem = null;
      this.$refs.optionsDialog.close();
    },

    async openEditDialog() {
      if (!this.selectedItem || !this.selectedItem.farmland_id) {
        this.showEditError("Finca no disponible");
        return;
      }

      this.isInitializingEditDialog = true;

      try {
        // Determinar tipo de finca
        this.editFarmType =
          this.selectedItem.farmland?.farm_type || "COMMERCIAL";

        // Cargar datos del item seleccionado
        this.editProductionMode = this.selectedItem.production_mode || "WEIGHT";
        this.editCategory =
          this.selectedItem.category === "-"
            ? null
            : this.selectedItem.category;
        this.editSubcategories = this.normalizeDefectSelection(
          this.selectedItem.berries_defects,
        );
        this.editFruitCount = this.selectedItem.fruit_count || 0;
        this.editUnits = this.selectedItem.units || 0;
        this.editCreatedOn = this.toDatetimeLocalString(
          this.selectedItem.created_on,
        );
        this.editUnitsWeightKg = this.selectedItem.units_weight
          ? Math.round((this.selectedItem.units_weight / 1000) * 1000) / 1000
          : 0;

        const targetFarmlandId =
          this.selectedItem.farmland?.id ||
          this.selectedItem.farmland_id ||
          null;
        const targetZoneId = this.selectedItem.zone?.id || null;

        // Finca y zona actuales
        this.editFarmlandId = targetFarmlandId;

        // Cargar zonas de la finca actual
        if (targetFarmlandId) {
          await this.loadZonesForFarmland(targetFarmlandId);
        } else {
          this.availableZones = [];
        }

        this.editZoneId =
          targetZoneId &&
          this.availableZones.some(
            (zone) => String(zone.id) === String(targetZoneId),
          )
            ? targetZoneId
            : null;

        // Cargar modos y categorías desde la API
        await this.loadModes();
        await this.loadCategoriesForMode(
          this.editProductionMode,
          this.editFarmlandId,
        );
        this.syncEditSubcategoriesWithOptions();

        // Cargar formatos para fincas comerciales o cooperativas en modo peso
        if (
          this.editFarmType === "COMMERCIAL" ||
          this.editProductionMode === "WEIGHT"
        ) {
          await this.loadFormatsForFarmland(this.selectedItem.farmland_id);

          if (this.formats.length === 0) {
            this.showEditError("No hay formatos disponibles para esta finca");
            return;
          }

          this.editFormatId =
            this.selectedItem.format_id ||
            (this.selectedItem.format && this.selectedItem.format.id);
        }

        this.$refs.editDialog.showModal();
      } catch (error) {
        // Detectar si el fallo es por falta de conexión y de caché
        const isOfflineNoCache =
          !error.response &&
          error.message &&
          error.message.includes("Sin conexión");
        this.showEditError(
          isOfflineNoCache
            ? "Sin conexión — los formatos no están disponibles. Para poder editar sin conexión, abre primero este diálogo estando online."
            : "Error al cargar los datos para edición",
        );
      } finally {
        this.isInitializingEditDialog = false;
      }
    },

    closeEditDialog() {
      this.$refs.editDialog.close();
      this.closeDialog();
      this.selectedItem = null;
    },

    closeEditConfirmDialog() {
      this.$refs.editConfirmDialog.close();
    },

    closeDeleteConfirmDialog() {
      this.$refs.deleteConfirmDialog.close();
    },

    // Carga de formatos por finca
    // No capturamos el error aquí: dejamos que burbujee a openEditDialog
    // para evitar mostrar dos diálogos de error simultáneos.
    async loadFormatsForFarmland(farmlandId) {
      const response = await this.$http.get(
        `${this.$hostname}/farmlands/${farmlandId}/formats/`,
      );
      this.formats = response.data;
    },

    async loadModes() {
      try {
        const response = await this.$http.get(`${this.$hostname}/modes/`);
        this.availableModes = response.data;
      } catch (e) {
        console.error("Error al cargar modos:", e);
        this.availableModes = [];
      }
    },

    async loadCategoriesForMode(modeCode, farmlandId = null) {
      if (!modeCode) {
        this.availableCategories = [];
        return;
      }
      try {
        const params = [`mode=${modeCode}`];
        if (farmlandId) {
          params.push(`farmland=${farmlandId}`);
        }
        const url = `${this.$hostname}/categories/?${params.join("&")}`;
        const response = await this.$http.get(url);
        this.availableCategories = response.data;

        // Auto-select if only one category available
        if (this.availableCategories.length === 1) {
          this.editCategory = this.availableCategories[0].code;
        }
      } catch (e) {
        console.error("Error al cargar categorías:", e);
        this.availableCategories = [];
      }
    },

    async loadFarmlands() {
      try {
        const response = await this.$http.get(`${this.$hostname}/farmlands/`);
        this.availableFarmlands = response.data;
      } catch (e) {
        console.error("Error al cargar fincas:", e);
        this.availableFarmlands = [];
      }
    },

    async loadZonesForFarmland(farmlandId) {
      if (!farmlandId) {
        this.availableZones = [];
        return;
      }
      try {
        const response = await this.$http.get(
          `${this.$hostname}/farmlands/${farmlandId}/zones/`,
        );
        this.availableZones = response.data;
      } catch (e) {
        console.error("Error al cargar zonas:", e);
        this.availableZones = [];
      }
    },

    // Mostrar mensajes
    showEditSuccess(message) {
      this.editIsError = false;
      this.editConfirmMessage = message;
      this.$refs.editConfirmDialog.showModal();
    },

    showEditError(message) {
      this.editIsError = true;
      this.editConfirmMessage = message;
      this.$refs.editConfirmDialog.showModal();
    },

    showDeleteSuccess(message) {
      this.deleteIsError = false;
      this.deleteConfirmMessage = message;
      this.$refs.deleteConfirmDialog.showModal();
    },

    normalizeDefectKey(value) {
      return String(value || "")
        .trim()
        .toLocaleLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ");
    },

    normalizeDefectSelection(rawValue) {
      if (!rawValue) return [];

      if (Array.isArray(rawValue)) {
        return rawValue
          .map((value) => String(value || "").trim())
          .filter((value) => value);
      }

      if (typeof rawValue === "object") {
        return Object.entries(rawValue)
          .filter(([, selected]) => Boolean(selected))
          .map(([key]) => String(key || "").trim())
          .filter((value) => value);
      }

      if (typeof rawValue === "string") {
        return rawValue
          .split(",")
          .map((value) => value.trim())
          .filter((value) => value);
      }

      return [];
    },

    syncEditSubcategoriesWithOptions() {
      const options = this.editSubcategoryOptions;
      if (!options.length) {
        this.editSubcategories = [];
        return;
      }

      const optionMap = new Map(
        options.map((option) => [this.normalizeDefectKey(option), option]),
      );

      const normalized = [];
      this.normalizeDefectSelection(this.editSubcategories).forEach((value) => {
        const canonical = optionMap.get(this.normalizeDefectKey(value));
        if (canonical && !normalized.includes(canonical)) {
          normalized.push(canonical);
        }
      });

      this.editSubcategories = normalized;
    },

    toDatetimeLocalString(isoStr) {
      if (!isoStr) return "";
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    async confirmEdit() {
      // Construir datos base
      const requestData = {
        production_id: this.selectedItem.id,
      };

      // Enviar finca si ha cambiado
      const currentFarmlandId =
        this.selectedItem.farmland?.id || this.selectedItem.farmland_id;
      if (
        this.editFarmlandId &&
        parseInt(this.editFarmlandId) !== parseInt(currentFarmlandId)
      ) {
        requestData.farmland_id = this.editFarmlandId;
      }

      // Enviar zona siempre (permite quitar zona con null/0)
      requestData.zone_id = this.editZoneId || 0;

      // Enviar fecha de creación si ha cambiado
      if (this.editCreatedOn) {
        requestData.created_on = this.editCreatedOn;
      }

      // FINCA COMERCIAL: solo formato y unidades
      if (this.editFarmType === "COMMERCIAL") {
        if (!this.editFormatId || !this.editUnits || this.editUnits <= 0) {
          this.showEditError("Por favor, complete formato y cantidad de cajas");
          return;
        }
        requestData.format_id = this.editFormatId;
        requestData.units = this.editUnits;
      }
      // FINCA COOPERATIVA: modo, categoría y datos específicos del modo
      else if (this.editFarmType === "COOPERATIVE") {
        // Validar modo y categoría
        if (!this.editProductionMode || !this.editCategory) {
          this.showEditError("Por favor, seleccione modo y categoría");
          return;
        }

        requestData.production_mode = this.editProductionMode;
        requestData.category = this.editCategory;

        if (this.isEditDefectCategory) {
          this.syncEditSubcategoriesWithOptions();
          if (!this.editSubcategories.length) {
            this.showEditError(
              "Por favor, seleccione al menos una subcategoría",
            );
            return;
          }
          requestData.berries_defects = this.editSubcategories;
        }

        // Modo PESO: formato y unidades
        else if (this.editProductionMode === "WEIGHT") {
          if (!this.editFormatId || !this.editUnits || this.editUnits <= 0) {
            this.showEditError(
              "Por favor, complete todos los campos válidos para modo por peso",
            );
            return;
          }
          requestData.format_id = this.editFormatId;
          requestData.units = this.editUnits;
        }
        // Modo CONTEO: número de frutas y kg totales
        else if (this.editProductionMode === "COUNT") {
          if (!this.editFruitCount || this.editFruitCount < 1) {
            this.showEditError(
              "Por favor, complete el número de frutas para modo por conteo",
            );
            return;
          }
          requestData.fruit_count = this.editFruitCount;
          requestData.units_weight = Math.round(
            (this.editUnitsWeightKg || 0) * 1000,
          );
        }
        // Modo LOTE: número de frutas estimado y kg totales
        else if (this.editProductionMode === "LOTE") {
          if (!this.editFruitCount || this.editFruitCount < 1) {
            this.showEditError(
              "Por favor, complete el número de frutas estimado para modo por lote",
            );
            return;
          }
          requestData.fruit_count = this.editFruitCount;
          requestData.units_weight = Math.round(
            (this.editUnitsWeightKg || 0) * 1000,
          );
        }
      }

      try {
        const response = await this.$http.patch(
          `${this.$hostname}/production/`,
          requestData,
        );

        // Operación encolada para sincronizar cuando haya conexión
        if (response.__queued) {
          this.closeEditDialog();
          this.closeDialog();
          this.showEditSuccess(
            "Cambio guardado. Se sincronizará automáticamente cuando recuperes la conexión.",
          );
          return;
        }

        if (response.status === 200) {
          this.closeEditDialog();
          this.closeDialog();
          this.showEditSuccess("Registro actualizado correctamente");
          await this.getProduction();
        } else {
          this.showEditError(
            response.data.error || "Error desconocido al actualizar",
          );
        }
      } catch (error) {
        let errorMsg = "Error al editar el registro";
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            errorMsg = error.response.data.error;
          } else if (error.response.data.message) {
            errorMsg = error.response.data.message;
          } else if (typeof error.response.data === "string") {
            errorMsg = error.response.data;
          } else {
            errorMsg = `Error ${error.response.status}: ${JSON.stringify(
              error.response.data,
            )}`;
          }
        }
        this.showEditError(errorMsg);
      }
    },

    async confirmDelete() {
      if (!this.selectedItem || !this.selectedItem.id) {
        this.showDeleteError("No hay registro seleccionado o ID inválido");
        return;
      }

      try {
        const response = await this.$http.delete(
          `${this.$hostname}/production/${this.selectedItem.id}/`, // Cambio: sin .data
          { headers: { "Content-Type": "application/json" } },
        );

        if (response.status === 204) {
          this.closeDialog();
          this.showDeleteSuccess("Registro eliminado correctamente");
          await this.getProduction();
        } else {
          this.showDeleteError("Error al eliminar el registro");
        }
      } catch (error) {
        let errorMsg = "Error al eliminar el registro";
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          errorMsg = error.response.data.error;
        }
        this.showDeleteError(errorMsg);
      }
    },
    async exportToCSV() {
      let rows;

      try {
        rows = await this.fetchAllProductionRows();
      } catch (error) {
        this.showEditError("Error al exportar producción diaria");
        return;
      }

      // Crear encabezados
      const headers = [
        "Finca",
        "Zona",
        "Código Zona",
        "Modo",
        "Categoría",
        "Defectos",
        "Formato",
        "Nº Cajas",
        "Nº Frutas",
        "Total (kg)",
        "Fecha",
      ];

      // Crear filas de datos
      const csvRows = rows.map((item) => [
        item.farmland?.name || "",
        item.zone?.name || "-",
        item.zone?.code || "-",
        item.production_mode_display || "-",
        item.category || "-",
        item.berries_defects_display || "-",
        item.format_display || "-",
        item.units_display !== undefined ? item.units_display : "-",
        item.fruit_count_display || "-",
        item.total_display !== undefined ? item.total_display : "-",
        this.formatDateTime(item.created_on),
      ]);

      // Combinar encabezados y filas
      const csvContent = [
        headers.join(","),
        ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      // Crear blob y descargar
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `produccion_${new Date().getTime()}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async exportToExcel() {
      let rows;

      try {
        rows = await this.fetchAllProductionRows();
      } catch (error) {
        this.showEditError("Error al exportar producción diaria");
        return;
      }

      const headers = [
        "Finca",
        "Zona",
        "Código Zona",
        "Modo",
        "Categoría",
        "Defectos",
        "Formato",
        "Nº Cajas",
        "Nº Frutas",
        "Total (kg)",
        "Fecha",
      ];

      const excelRows = rows.map((item) => [
        item.farmland?.name || "",
        item.zone?.name || "-",
        item.zone?.code || "-",
        item.production_mode_display || "-",
        item.category || "-",
        item.berries_defects_display || "-",
        item.format_display || "-",
        item.units_display !== undefined ? item.units_display : "-",
        item.fruit_count_display || "-",
        item.total_display !== undefined ? item.total_display : "-",
        this.formatDateTime(item.created_on),
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produccion diaria");
      XLSX.writeFile(workbook, `produccion_${new Date().getTime()}.xlsx`);
    },
  },
};
</script>

<style>
.hidden-id-field {
  display: none;
}

.table-container {
  position: relative;
}

.production-list-daily {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.production-list-daily .table-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.production-list-daily .table-container > .vuetable-body-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
}

.production-list-daily
  .table-container
  > .vuetable-body-wrapper
  > .vuetable-body-wrapper {
  height: 100% !important;
  min-height: 0;
}

.control-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0 16px;
}

.production-list-daily .control-pagination {
  flex-shrink: 0;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.pagination-btn {
  min-width: 34px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #107631;
  color: #107631;
}

.pagination-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pagination-status {
  min-width: 150px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #475467;
}

.subcategory-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.subcategory-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}

/* Ocultar botón móvil en escritorio */
.mobile-export-btn {
  display: none !important;
}

.daily-export-desktop-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin: 0 16px 10px 16px;
}

.desktop-export-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #fafafa !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  box-shadow: none !important;
}

.desktop-export-btn:hover {
  background-color: #f5f5f5 !important;
  border-color: #999 !important;
  color: #333 !important;
  box-shadow: none !important;
  transform: none !important;
}

@media (max-width: 768px) {
  .daily-export-mobile-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0 auto 15px auto;
  }

  .mobile-export-btn {
    display: inline-flex !important;
    justify-content: center;
    margin: 0;
    text-align: center;
    width: auto;
    min-width: 140px;
    max-width: 170px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 6px rgba(16, 118, 49, 0.3);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .mobile-export-btn--compact {
    min-width: 138px;
    max-width: 160px;
    padding: 10px 14px;
  }

  .mobile-export-btn:hover {
    background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
    box-shadow: 0 6px 8px rgba(16, 118, 49, 0.4);
    transform: translateY(-1px);
  }

  /* Ocultar botón de escritorio en móvil */
  .daily-export-desktop-actions {
    display: none !important;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .control-pagination {
    flex-wrap: wrap;
    padding: 10px 0 14px;
  }

  .pagination-status {
    width: 100%;
    order: -1;
    margin-bottom: 6px;
  }

  .vuetable-body-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vuetable {
    font-size: 12px;
    min-width: 600px;
  }

  .vuetable th,
  .vuetable td {
    font-size: 11px;
    padding: 8px 6px;
    white-space: nowrap;
  }
}
</style>
